import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "space-y-4 p-4 sm:p-6 lg:p-8" }
const _hoisted_2 = { class: "flex items-center text-xl font-semibold text-gray-800" }
const _hoisted_3 = { class: "flex space-x-4" }
const _hoisted_4 = { class: "space-y-4" }
const _hoisted_5 = { class: "grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" }
const _hoisted_6 = { class: "mb-1 font-semibold" }
const _hoisted_7 = { class: "mt-2 flex flex-wrap justify-between" }
const _hoisted_8 = { class: "flex items-center space-x-1 text-xs text-gray-600" }
const _hoisted_9 = {
  key: 0,
  class: "ml-1"
}
const _hoisted_10 = { key: 1 }
const _hoisted_11 = { class: "flex items-center space-x-1 text-xs text-gray-600" }
const _hoisted_12 = { class: "ml-1" }
const _hoisted_13 = { class: "grid grid-cols-1 gap-4 lg:grid-cols-2" }
const _hoisted_14 = { class: "mb-4 text-base font-semibold text-gray-800" }
const _hoisted_15 = { key: 0 }
const _hoisted_16 = { class: "mb-4 text-base font-semibold text-gray-800" }
const _hoisted_17 = { class: "flex flex-col gap-6 md:flex-row md:items-start" }
const _hoisted_18 = { class: "flex-1" }
const _hoisted_19 = { class: "text-muted-foreground text-xs font-semibold uppercase tracking-wider" }
const _hoisted_20 = { class: "mt-2 text-sm leading-relaxed" }
const _hoisted_21 = { class: "text-center text-xs font-medium text-gray-500" }
const _hoisted_22 = { class: "mt-1 text-center text-2xl font-semibold text-gray-800" }
const _hoisted_23 = { class: "mt-6 rounded-lg border bg-gray-300/40 p-4" }
const _hoisted_24 = { class: "text-xs font-bold uppercase text-gray-600" }
const _hoisted_25 = { class: "mt-2 text-sm leading-relaxed text-gray-600" }
const _hoisted_26 = { class: "mt-4 grid gap-3 md:grid-cols-3" }
const _hoisted_27 = { class: "text-xs uppercase text-gray-500" }
const _hoisted_28 = { class: "mt-1 text-base font-semibold" }
const _hoisted_29 = { class: "mt-1 text-xs text-gray-600" }
const _hoisted_30 = { class: "mt-3 flex flex-wrap gap-1.5" }
const _hoisted_31 = { class: "space-y-4 pt-4" }
const _hoisted_32 = { class: "flex flex-wrap items-center justify-between gap-4" }
const _hoisted_33 = { class: "text-lg font-semibold text-gray-800" }
const _hoisted_34 = {
  role: "list",
  class: "my-2 space-y-2"
}
const _hoisted_35 = {
  key: 0,
  class: "mt-0.5 flex h-48 flex-col flex-wrap items-center justify-center rounded-b bg-gray-200 font-semibold text-gray-600"
}

import { ContactService, toastServiceError } from '@/services';
import { useContactTimelineFilterStore, useFilterStore, useUiStore } from '@/stores';
import { CallModel, ContactModel, ContactTimelineStats, DataItem, Permission } from '@/types';
import { applyPhoneMask } from '@/utils/number';
import { BookOpenIcon, MailIcon, PhoneIcon } from '@heroicons/vue/solid';
import { computed, onMounted, ref, Ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import {
    ContactAuraPanel,
    ContactHealthScoreChart,
    ContactTimelineCallDetails,
    ContactTimelineFilters,
} from '@/views/pages/contact/components';
import moment from 'moment';
import { ContactTimelineModel } from '@/types/models/ContactTimelineModel';
import { formatSecToTime } from '@/utils/datetime';
import { useI18n } from 'vue-i18n';
import { ContactHealthScoreStats } from '@/types/metrics/ContactHealthScore';
import { timelineAnalysisMock } from './mocks/TimelineAnalysis';

const showAskAuraButton = false;

const perPage = 20;

export default /*@__PURE__*/_defineComponent({
  __name: 'ContactTimelinePage',
  setup(__props) {

const route = useRoute();
const uiStore = useUiStore();
const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);
const { t } = useI18n();

const contact: Ref<ContactModel | undefined> = ref(undefined);
const timelineStats: Ref<ContactTimelineStats | undefined> = ref(undefined);
const calls: Ref<CallModel[]> = ref([]);
const count = ref(0);
const currentPage = ref(1);

const filterStore = useFilterStore();
const timelineStore = useContactTimelineFilterStore();

const dateRange = computed<Array<Date>>({
    get: () => filterStore.dateRange,
    set: (range) => filterStore.setDateRange(range),
});

const mainEmail = computed(() => contact.value?.emails.find((email) => email.isMain) ?? null);

const mainPhone = computed(() => contact.value?.phones.find((phone) => phone.isMain) ?? null);

const fetchContact = async (id: number) => {
    await setLoading(true);
    try {
        const response = await ContactService.get<ContactModel>(id);
        contact.value = response.data;
    } catch (error) {
        toastServiceError(error);
    } finally {
        await setLoading(false);
    }
};

const fetchContactCalls = async (id: number) => {
    await setLoading(true);
    try {
        const response = await ContactService.getTimeline<ContactTimelineModel>(id, {
            params: {
                startDate: moment.utc(dateRange.value[0]).format('YYYY-MM-DD'),
                endDate: moment.utc(dateRange.value[1]).format('YYYY-MM-DD'),
                callType: timelineStore.callType?.value ?? null,
                page: currentPage.value,
                per_page: perPage,
            },
        });
        calls.value = response.data.calls;
        timelineStats.value = response.data.stats;
        count.value = Number(response.headers['x-pagination-count']);
    } catch (error) {
        toastServiceError(error);
    } finally {
        await setLoading(false);
    }
};

const daysSinceLastContact = computed(() => {
    if (!timelineStats.value?.lastContactAt) return null;
    const lastContactDate = moment(timelineStats.value.lastContactAt);
    const today = moment();
    return today.diff(lastContactDate, 'days');
});

const healthScore: ContactHealthScoreStats = {
    score: 59,
    analysis:
        'A relação com a Fintrust Capital vinha estável sob tratativas de integração e renovação contratual, mas sofreu uma queda recente de satisfação que reduziu o health score para 59. O principal ponto de atrito foi uma indisponibilidade de 40 minutos no sistema em horário de pico, o que gerou forte insatisfação e a exigência de reembolso e de um relatório de causa raiz. Como próxima ação recomendada, deve-se formalizar o envio do relatório técnico de causa raiz e conceder o crédito na fatura solicitado para restabelecer a confiança do cliente antes da renovação de fim de ano.',
};

const headerStats = computed<DataItem<string>[]>(() => {
    if (!timelineStats.value) return [];

    return [
        {
            name: 'totalCalls',
            value: timelineStats.value.totalCalls.toString(),
        },
        {
            name: 'averageDuration',
            value: formatSecToTime(timelineStats.value.averageDuration, true),
        },
        {
            name: 'positivePercentage',
            value:
                timelineStats.value.positivePercentage !== null
                    ? `${timelineStats.value.positivePercentage}%`
                    : '-',
        },
        {
            name: 'daysSinceLastContact',
            value: t('contact.timeline.stats.daysCount', { n: daysSinceLastContact.value ?? 0 }),
        },
    ];
});

const factors = timelineAnalysisMock.factors;

watch(
    [() => filterStore.dateRange, () => timelineStore.callType],
    () => {
        const contactId = Number(route.params.id);
        if (currentPage.value === 1) fetchContactCalls(contactId);
        else currentPage.value = 1;
    },
    { deep: true },
);

watch(currentPage, () => {
    const contactId = Number(route.params.id);
    if (!isNaN(contactId)) fetchContactCalls(contactId);
});

onMounted(() => {
    const contactId = Number(route.params.id);
    if (!isNaN(contactId)) {
        fetchContact(contactId);
        fetchContactCalls(contactId);
    }
});

return (_ctx: any,_cache: any) => {
  const _component_UiBackButton = _resolveComponent("UiBackButton")!
  const _component_UiButton = _resolveComponent("UiButton")!
  const _component_router_link = _resolveComponent("router-link")!
  const _component_UiPageHeader = _resolveComponent("UiPageHeader")!
  const _component_UiPanel = _resolveComponent("UiPanel")!
  const _component_UiPagination = _resolveComponent("UiPagination")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_UiPageHeader, null, {
      info: _withCtx(() => [
        _createElementVNode("h1", _hoisted_2, [
          _createVNode(_unref(BookOpenIcon), {
            class: "mr-1 h-6 w-6 text-gray-500",
            "aria-hidden": "true"
          }),
          _createTextVNode(" " + _toDisplayString(_ctx.$t('contact.timeline.section.contact.title')), 1)
        ])
      ]),
      actions: _withCtx(() => [
        _createElementVNode("div", _hoisted_3, [
          _createVNode(_component_UiBackButton, { fallbackTo: { name: 'ListContact' } }),
          showAskAuraButton
            ? (_openBlock(), _createBlock(_unref(ContactAuraPanel), { key: 0 }))
            : _createCommentVNode("", true),
          (_ctx.$can(_unref(Permission).EDIT_CONTACT))
            ? (_openBlock(), _createBlock(_component_router_link, {
                key: 1,
                to: {
                            name: 'EditContactById',
                            params: {
                                id: _unref(route).params.id,
                            },
                        },
                class: "block hover:bg-gray-50"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_UiButton, {
                    variant: "primary",
                    "text-variant": "white"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$t('core.actions.Edit')), 1)
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["to"]))
            : _createCommentVNode("", true)
        ])
      ]),
      _: 1
    }),
    _createElementVNode("div", _hoisted_4, [
      _createVNode(_component_UiPanel, null, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("div", null, [
              _createElementVNode("h3", _hoisted_6, _toDisplayString(_ctx.$t('contact.timeline.section.contact.name')), 1),
              _createElementVNode("p", null, _toDisplayString(contact.value?.name), 1)
            ])
          ]),
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("div", _hoisted_8, [
              _createElementVNode("span", null, _toDisplayString(_ctx.$t('contact.timeline.section.contact.company')) + ": ", 1),
              (contact.value?.contactCompany)
                ? (_openBlock(), _createElementBlock("div", _hoisted_9, _toDisplayString(contact.value?.contactCompany), 1))
                : (_openBlock(), _createElementBlock("span", _hoisted_10, "-"))
            ]),
            _createElementVNode("div", _hoisted_11, [
              _createElementVNode("span", null, _toDisplayString(_ctx.$t('contact.timeline.section.contact.ownerUser')) + ": ", 1),
              _createElementVNode("div", _hoisted_12, _toDisplayString(contact.value?.ownerUser.name), 1)
            ])
          ])
        ]),
        _: 1
      }),
      _createElementVNode("div", _hoisted_13, [
        _createVNode(_component_UiPanel, null, {
          default: _withCtx(() => [
            _createElementVNode("h3", _hoisted_14, _toDisplayString(_ctx.$t('contact.timeline.section.email')), 1),
            (contact.value?.emails.length === 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                  _createElementVNode("p", null, _toDisplayString(_ctx.$t('contact.errors.noEmails')), 1)
                ]))
              : (_openBlock(), _createBlock(_component_UiPanel, {
                  key: 1,
                  class: "flex items-center bg-white"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(MailIcon), {
                      class: "mr-1.5 h-5 w-5 flex-shrink-0 text-gray-400",
                      "aria-hidden": "true"
                    }),
                    _createTextVNode(" " + _toDisplayString(mainEmail.value?.email), 1)
                  ]),
                  _: 1
                }))
          ]),
          _: 1
        }),
        _createVNode(_component_UiPanel, null, {
          default: _withCtx(() => [
            _createElementVNode("h3", _hoisted_16, _toDisplayString(_ctx.$t('contact.timeline.section.phone')), 1),
            (mainPhone.value)
              ? (_openBlock(), _createBlock(_component_UiPanel, {
                  key: 0,
                  class: "flex items-center bg-white"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(PhoneIcon), {
                      class: "mr-1.5 h-5 w-5 flex-shrink-0 text-gray-400",
                      "aria-hidden": "true"
                    }),
                    _createTextVNode(" " + _toDisplayString(_unref(applyPhoneMask)(mainPhone.value?.number)), 1)
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true)
          ]),
          _: 1
        })
      ])
    ]),
    _createVNode(_component_UiPanel, null, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_17, [
          _createVNode(_unref(ContactHealthScoreChart), { healthScore: healthScore }),
          _createElementVNode("div", _hoisted_18, [
            _createElementVNode("h2", _hoisted_19, _toDisplayString(_ctx.$t('contact.timeline.stats.analysis.title')), 1),
            _createElementVNode("p", _hoisted_20, _toDisplayString(healthScore?.analysis), 1)
          ])
        ]),
        (headerStats.value.length)
          ? (_openBlock(), _createElementBlock("dl", {
              key: 0,
              class: _normalizeClass(["mt-6 grid grid-cols-2 gap-4", ['sm:grid-cols-' + headerStats.value.length]])
            }, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(headerStats.value, (stat) => {
                return (_openBlock(), _createBlock(_component_UiPanel, {
                  key: stat.name
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("dt", _hoisted_21, _toDisplayString(_ctx.$t('contact.timeline.stats.' + stat.name)), 1),
                    _createElementVNode("dd", _hoisted_22, _toDisplayString(stat.value), 1)
                  ]),
                  _: 2
                }, 1024))
              }), 128))
            ], 2))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_23, [
          _createElementVNode("h3", _hoisted_24, _toDisplayString(_ctx.$t('contact.timeline.stats.analysis.healthScoreCalculation.title')), 1),
          _createElementVNode("p", _hoisted_25, _toDisplayString(_ctx.$t('contact.timeline.stats.analysis.healthScoreCalculation.description')), 1),
          _createElementVNode("ul", _hoisted_26, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(factors), (factor) => {
              return (_openBlock(), _createElementBlock("li", {
                key: factor.key,
                class: "bg-card rounded-lg border border-gray-300 bg-white p-4"
              }, [
                _createElementVNode("p", _hoisted_27, _toDisplayString(_ctx.$t(
                                    'contact.timeline.stats.analysis.healthScoreCalculation.factors.' +
                                        factor.key +
                                        '.title',
                                )), 1),
                _createElementVNode("p", _hoisted_28, _toDisplayString(factor.value), 1),
                _createElementVNode("p", _hoisted_29, [
                  (factor.key === 'predominantSentiment')
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        _createTextVNode(_toDisplayString(_ctx.$t(
                                        'contact.timeline.stats.analysis.healthScoreCalculation.factors.' +
                                            factor.key +
                                            '.description',
                                        {
                                            count: factor.count,
                                            percentage: factor.percentage,
                                        },
                                    )), 1)
                      ], 64))
                    : (factor.key === 'estimatedSatisfaction')
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                          _createTextVNode(_toDisplayString(_ctx.$t(
                                        'contact.timeline.stats.analysis.healthScoreCalculation.factors.' +
                                            factor.key +
                                            '.description',
                                        {
                                            count: factor.count,
                                        },
                                    )), 1)
                        ], 64))
                      : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                          _createTextVNode(_toDisplayString(_ctx.$t(
                                        `contact.timeline.stats.analysis.healthScoreCalculation.factors.${factor.key}.${factor.trend}`,
                                    )), 1)
                        ], 64)),
                  _createElementVNode("template", _hoisted_30, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(factor.evidence, (evidence) => {
                      return (_openBlock(), _createBlock(_component_UiButton, {
                        key: evidence.id,
                        class: "text-primary rounded border px-2 py-1 text-[11px] font-medium transition-colors hover:bg-gray-100"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(evidence.label) + " " + _toDisplayString(evidence.satisfaction), 1)
                        ]),
                        _: 2
                      }, 1024))
                    }), 128))
                  ])
                ])
              ]))
            }), 128))
          ])
        ])
      ]),
      _: 1
    }),
    _createElementVNode("div", _hoisted_31, [
      _createElementVNode("div", _hoisted_32, [
        _createElementVNode("h2", _hoisted_33, _toDisplayString(_ctx.$t('contact.timeline.title')), 1),
        _createVNode(_unref(ContactTimelineFilters))
      ]),
      _createElementVNode("ul", _hoisted_34, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(calls.value, (call) => {
          return (_openBlock(), _createBlock(_unref(ContactTimelineCallDetails), {
            key: call.id,
            call: call
          }, null, 8, ["call"]))
        }), 128)),
        (!calls.value.length)
          ? (_openBlock(), _createElementBlock("li", _hoisted_35, _toDisplayString(_ctx.$t('core.messages.noRecordsFound')), 1))
          : _createCommentVNode("", true)
      ]),
      (calls.value.length)
        ? (_openBlock(), _createBlock(_component_UiPagination, {
            key: 0,
            modelValue: currentPage.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((currentPage).value = $event)),
            count: count.value,
            perPage: perPage
          }, null, 8, ["modelValue", "count"]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})